const round2 = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toNonNegativeNumber = (value) => Math.max(0, toNumber(value));

const normalizeVacationScheme = (value) => {
  const scheme = String(value || '').trim().toLowerCase();
  return scheme === 'holiday_allowance' ? 'holiday_allowance' : 'paid_vacation_days';
};

/**
 * @typedef {Object} PayslipEngineInput
 * @property {string} staffEngagementType
 * @property {string} compensationBasis
 * @property {number|string} baseMonthlySalaryAmount
 * @property {number|string} commissionAmount
 * @property {number|string} registeredHours
 * @property {number|string} expectedHours
 * @property {number|string} pensionPercent
 * @property {number|string} taxFradrag
 * @property {number|string} taxTrakprocent
 * @property {number|string} vacationScheme
 * @property {number|string} paidVacationDays
 * @property {number|string} holidayAllowancePercent
 * @property {number|string} vacationAccrualPerMonth
 */

/**
 * @typedef {Object} PayslipEngineResult
 * @property {number} baseSalaryGross
 * @property {number} grossPay
 * @property {number} commissionAmount
 * @property {number} amIncome
 * @property {number} amBidrag
 * @property {number} aIncome
 * @property {number} personFradrag
 * @property {number} skattegrundlag
 * @property {number} aSkat
 * @property {number} atpEmployee
 * @property {number} atpEmployer
 * @property {number} pensionEmployee
 * @property {number} pensionEmployer
 * @property {number} netSalary
 * @property {{ scheme: string, daysAccrued: number, grossAllowance: number, amBidrag: number, aSkat: number, netAllowance: number }} vacation
 * @property {{ '0013': number, '0015': number, '0016': number, '0046': number, '147': number, '148': number, '0200': number, '202': number }} eIndkomst
 */

/**
 * Calculate the payslip payroll values from normalized staff input.
 *
 * @param {PayslipEngineInput} input
 * @returns {PayslipEngineResult}
 */
export const calculatePayslipPayroll = (input = {}) => {
  const engagementType = String(input.staffEngagementType || 'employee').trim().toLowerCase();
  const compensationBasis = String(input.compensationBasis || 'fixed_salary').trim().toLowerCase();
  const vacationScheme = normalizeVacationScheme(input.vacationScheme);

  const baseMonthlySalaryAmount = toNonNegativeNumber(input.baseMonthlySalaryAmount);
  const commissionAmount = toNonNegativeNumber(input.commissionAmount);
  const registeredHours = toNonNegativeNumber(input.registeredHours);
  const expectedHours = toNonNegativeNumber(input.expectedHours);
  const pensionPercent = toNonNegativeNumber(input.pensionPercent);
  const employerPensionPercent = toNonNegativeNumber(input.employerPensionPercent);
  const personFradrag = toNonNegativeNumber(input.taxFradrag);
  const taxTrakprocent = toNonNegativeNumber(input.taxTrakprocent);
  const holidayAllowancePercent = toNonNegativeNumber(input.holidayAllowancePercent) || 12.5;
  const vacationAccrualPerMonth = toNonNegativeNumber(input.vacationAccrualPerMonth) || 2.08;

  const baseHourlyRate = expectedHours > 0 ? baseMonthlySalaryAmount / expectedHours : 0;
  const baseSalaryGross = expectedHours > 0 ? baseHourlyRate * registeredHours : baseMonthlySalaryAmount;

  const grossPay = round2(baseSalaryGross + commissionAmount);
  const hasEmployeeStylePayroll = engagementType !== 'subcontractor';

  const pensionEmployee = hasEmployeeStylePayroll && pensionPercent > 0
    ? round2(grossPay * (pensionPercent / 100))
    : 0;
  const pensionEmployer = hasEmployeeStylePayroll && pensionPercent > 0
    ? round2(grossPay * (employerPensionPercent / 100))
    : 0;

  const amIncome = hasEmployeeStylePayroll
    ? round2(Math.max(0, grossPay - pensionEmployee))
    : round2(grossPay);
  const amBidrag = hasEmployeeStylePayroll
    ? round2(amIncome * 0.08)
    : 0;
  const aIncome = hasEmployeeStylePayroll
    ? round2(Math.max(0, amIncome - amBidrag))
    : round2(grossPay);
  const skattegrundlag = hasEmployeeStylePayroll
    ? round2(Math.max(0, aIncome - personFradrag))
    : 0;
  const aSkat = hasEmployeeStylePayroll
    ? round2(skattegrundlag * (taxTrakprocent / 100))
    : 0;

  const atpEmployee = hasEmployeeStylePayroll ? 94.65 : 0;
  const atpEmployer = hasEmployeeStylePayroll ? 189.35 : 0;

  const netSalary = round2(Math.max(0, grossPay - amBidrag - aSkat - atpEmployee - pensionEmployee));

  const ferieberettigetIndkomst = round2(grossPay);
  const grossAllowance = vacationScheme === 'holiday_allowance'
    ? round2(ferieberettigetIndkomst * (holidayAllowancePercent / 100))
    : 0;
  const vacationAmBidrag = grossAllowance > 0 ? round2(grossAllowance * 0.08) : 0;
  const vacationASkat = grossAllowance > 0
    ? round2(Math.max(0, grossAllowance - vacationAmBidrag) * (taxTrakprocent / 100))
    : 0;
  const netAllowance = round2(Math.max(0, grossAllowance - vacationAmBidrag - vacationASkat));

  const bruttolon = grossPay;
  const medarbejderPension = pensionEmployee;
  const amIndkomst = amIncome;
  const amBidragValue = amBidrag;
  const aIndkomst = aIncome;
  const skattegrundlagValue = skattegrundlag;
  const aSkatValue = aSkat;
  const atpMedarbejder = atpEmployee;
  const nettolon = netSalary;
  const bruttoFeriepenge = vacationScheme === 'holiday_allowance' ? grossAllowance : 0;
  const amBidragFeriepenge = vacationScheme === 'holiday_allowance' ? vacationAmBidrag : 0;
  const aSkatFeriepenge = vacationScheme === 'holiday_allowance' ? vacationASkat : 0;
  const nettoFeriepenge = vacationScheme === 'holiday_allowance' ? netAllowance : 0;

  const eIndkomst = {
    '0013': amIndkomst,
    '0015': aSkatValue,
    '0016': amBidragValue,
    '0046': round2(atpEmployee + atpEmployer),
    '147': medarbejderPension,
    '148': pensionEmployer,
    '0200': vacationScheme === 'holiday_allowance' ? bruttoFeriepenge : round2(vacationAccrualPerMonth),
    '202': vacationScheme === 'holiday_allowance' ? nettoFeriepenge : 0,
  };

  return {
    baseSalaryGross: round2(baseSalaryGross),
    grossPay,
    bruttoløn: bruttolon,
    commissionAmount: round2(commissionAmount),
    amIncome,
    AM_indkomst: amIndkomst,
    amBidrag,
    AM_bidrag: amBidragValue,
    aIncome,
    A_indkomst: aIndkomst,
    personFradrag,
    skattegrundlag,
    skattegrundlag_value: skattegrundlagValue,
    aSkat,
    A_skat: aSkatValue,
    atpEmployee,
    ATP_medarbejder: atpMedarbejder,
    atpEmployer,
    pensionEmployee,
    medarbejder_pension: medarbejderPension,
    pensionEmployer,
    nettoløn: nettolon,
    netSalary,
    vacation: {
      scheme: vacationScheme,
      daysAccrued: round2(vacationAccrualPerMonth),
      grossAllowance,
      brutto_feriepenge: bruttoFeriepenge,
      amBidrag: vacationAmBidrag,
      AM_bidrag_feriepenge: amBidragFeriepenge,
      aSkat: vacationASkat,
      A_skat_feriepenge: aSkatFeriepenge,
      netAllowance,
      netto_feriepenge: nettoFeriepenge,
    },
    brutto_feriepenge: bruttoFeriepenge,
    AM_bidrag_feriepenge: amBidragFeriepenge,
    A_skat_feriepenge: aSkatFeriepenge,
    netto_feriepenge: nettoFeriepenge,
    eIndkomst,
  };
};
